import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'outlineWhite'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  as?: 'button' | 'a'
  href?: string
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'border-2 border-transparent bg-primary-600 text-white hover:bg-primary-700 focus-visible:ring-primary-500',
  secondary:
    'border-2 border-transparent bg-gray-100 text-gray-900 hover:bg-gray-200 focus-visible:ring-gray-400',
  outline:
    'border-2 border-primary-600 text-primary-600 hover:bg-primary-50 focus-visible:ring-primary-500',
  ghost:
    'border-2 border-transparent text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-400',
  outlineWhite:
    'border-2 border-white text-white hover:bg-white/10 focus-visible:ring-white',
}

const getBaseClasses = (as: 'button' | 'a') => cn(
  // Use 'flex' for anchor to ensure full clickable area, 'inline-flex' for button
  as === 'a' 
    ? 'flex cursor-pointer items-center justify-center gap-2 rounded-pill font-semibold transition-all duration-normal'
    : 'inline-flex cursor-pointer items-center justify-center gap-2 rounded-pill font-semibold transition-all duration-normal',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
  'disabled:pointer-events-none disabled:opacity-50'
)

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-2.5 text-base',
  lg: 'px-8 py-3 text-lg',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', as = 'button', href, children, ...props }, ref) => {
    const classes = cn(
      getBaseClasses(as),
      // Ensure anchor buttons don't have underline
      as === 'a' && 'no-underline',
      variantClasses[variant],
      sizeClasses[size],
      className
    )

    if (as === 'a' && href) {
      return (
        <a href={href} className={classes} {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
          {children}
        </a>
      )
    }

    return (
      <button ref={ref} className={classes} {...props}>
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
